import { UnloadTilesPlugin } from '3d-tiles-renderer/three/plugins';
import { Box3, Camera, Intersection, Raycaster, Vector3, WebGLRenderer } from 'three';

import { CloudFrontSigner } from '../../../../utils/cloud-front-signer/cloud-front-signer';
import { isDefined } from '../../../../utils/variables';
import { CoordinateManager } from '../../../coordinate-manager';
import { publish, SubscriptionTopic } from '../../../messaging/pubsub';
import { toPointPositions } from '../../../utilities/pick-near-cursor';
import { Tiled3dModel } from '../type';
import { ScreenFractionTilesRenderer } from './screen-fraction-tiles-renderer';
import { getLodSettings, LodProfile, selectLodProfile } from './tiles-lod';

/**
 *  Cesium point cloud handler.
 */
export class Cesium3dTilesRenderer extends Tiled3dModel {
  private tilesRenderer?: ScreenFractionTilesRenderer;
  private camera?: Camera;
  private lodProfile?: LodProfile;
  private unloadTilesPlugin?: UnloadTilesPlugin;

  constructor(
    private signer: CloudFrontSigner,
    readonly origo: Vector3,
  ) {
    super();
  }

  getType(): '3d-tiles-renderer' | 'skymap-tiles-renderer' | 'gltf-tiles-renderer' {
    return '3d-tiles-renderer';
  }

  /**
   *  Loads the root model and extracts the geometry center point.
   */
  async activate() {
    // We assume that the root file is always called "tileset.json" backend should ensure this naming.
    this.tilesRenderer = new ScreenFractionTilesRenderer(`${this.signer.getBaseUrl()}tileset.json`);

    this.applyLodProfile('probe');

    this.tilesRenderer.addEventListener('load-tileset', (e: unknown) => {
      const { tileset } = e as { tileset?: { root?: { content?: unknown } } };

      // The probe needs content in the root tile to measure, otherwise it would never load anything.
      if (this.lodProfile === 'probe' && !tileset?.root?.content) {
        this.applyLodProfile('light');
      }

      publish(SubscriptionTopic.OnSceneBoundingBoxChanged, { boundingBox: this.getBoundingBox() });
    });

    this.tilesRenderer.addEventListener('load-model', (e) => {
      // The tile is at its RTC_CENTER, in project coordinates, while the tileset's bounding volumes
      // are around the model's origo. Drawn around the origo too, the tiles stay within their
      // bounding volumes wherever the model is placed, see Tiled3DModelHandle, which the culling,
      // LOD selection and picking go by.
      e.scene.position.sub(this.origo);

      // Content bounds in the tiles group frame, used for the LOD selection of the heavy profile.
      // The tile is only added to the group once shown, so its world matrix is in that frame.
      e.scene.updateMatrixWorld(true);
      const contentBox = new Box3().setFromObject(e.scene);
      this.tilesRenderer?.contentBounds.set(e.tile, contentBox);

      if (this.lodProfile === 'probe') {
        this.applyLodProfile(
          selectLodProfile(this.tilesRenderer?.lruCache.getMemoryUsage(e.tile) ?? 0),
        );
      }
    });

    this.tilesRenderer.registerPlugin({
      preprocessURL: (uri: URL | string) => {
        const value = uri.toString();

        // Don't sign the blobs. Textures are loaded via blobs
        if (value.startsWith('blob:')) {
          return value;
        }

        // Remove the base URL part of the path in order to get the correct relative path for cloudfront.
        const relativePath = value.replace(this.signer.getBaseUrl(), '');
        return this.signer.getSignedRequest(relativePath);
      },
    });

    this.add(this.tilesRenderer.group);

    return Promise.resolve();
  }

  /**
   * Applies the LOD load/unload settings of the profile, see tiles-lod.
   */
  private applyLodProfile(profile: LodProfile) {
    const tilesRenderer = this.tilesRenderer;
    if (!tilesRenderer || this.lodProfile === profile) {
      return;
    }
    this.lodProfile = profile;

    const settings = getLodSettings(profile);
    tilesRenderer.useScreenFraction = settings.useScreenFraction;
    tilesRenderer.minLoadVisibleFraction = settings.minLoadVisibleFraction;
    tilesRenderer.errorTarget = settings.errorTarget;
    tilesRenderer.optimizedLoadStrategy = settings.optimizedLoadStrategy;
    tilesRenderer.loadSiblings = settings.loadSiblings;
    tilesRenderer.lruCache.minBytesSize = settings.lruCache.minBytesSize;
    tilesRenderer.lruCache.maxBytesSize = settings.lruCache.maxBytesSize;
    tilesRenderer.downloadQueue.maxJobs = settings.maxDownloadJobs;
    tilesRenderer.parseQueue.maxJobs = settings.maxParseJobs;

    if (this.unloadTilesPlugin) {
      tilesRenderer.unregisterPlugin(this.unloadTilesPlugin);
      this.unloadTilesPlugin = undefined;
    }
    if (settings.unloadTiles) {
      this.unloadTilesPlugin = new UnloadTilesPlugin(settings.unloadTiles);
      tilesRenderer.registerPlugin(this.unloadTilesPlugin);
    }
  }

  async loadLodChunks() {
    // Do nothing, this is handled by the tiles renderer.
  }

  setCameraAndResolution(camera: Camera, renderer: WebGLRenderer) {
    if (!this.tilesRenderer) {
      return;
    }

    // setCamera only registers cameras, so a replaced camera would otherwise keep driving the LOD selection.
    if (this.camera && this.camera !== camera) {
      this.tilesRenderer.deleteCamera(this.camera);
    }

    this.camera = camera;
    this.tilesRenderer.setCamera(camera);
    this.tilesRenderer.setResolutionFromRenderer(camera, renderer);
  }

  update() {
    this.tilesRenderer?.update();
  }

  cullLodChunks() {
    // Do nothing, this is handled by the tiles renderer.
  }

  isLoading(): boolean {
    return false;
  }

  /** Every hit of the ray, front-most first. */
  pick(ray: Raycaster): Intersection[] {
    const intersections = ray.intersectObject(this, true);
    return intersections.filter((intersection) => intersection.object.name !== 'temp');
  }

  intersect(ray: Raycaster): Intersection[] {
    return toPointPositions(ray, this.pick(ray));
  }

  available() {
    return true;
  }

  getBoundingBox() {
    if (!isDefined(this.tilesRenderer)) {
      return new Box3();
    }

    const boundingBox = new Box3();
    const result = this.tilesRenderer.getBoundingBox(boundingBox);

    // This happens if for example the tiled model haven't had time to initialize yet.
    if (!result) {
      return new Box3();
    }

    // Around the model's origo, placed where that is in the scene, see Tiled3DModelHandle
    const projectionOrigo = CoordinateManager.getInstance().getProjectionOrigo() ?? new Vector3();
    return boundingBox.translate(this.origo.clone().sub(projectionOrigo));
  }

  getOrigo() {
    return this.origo;
  }

  clearCustom() {
    this.tilesRenderer?.dispose();
    this.tilesRenderer = undefined;
    this.lodProfile = undefined;
    this.unloadTilesPlugin = undefined;
    this.camera = undefined;
  }
}
